import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Menu, MenuItem, useMediaQuery, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import LeftSidebar from './LeftSidebar';
import MobileHeader from './MobileHeader';
import TopAppBar from './TopAppBar';
import MobileMenu from './MobileMenu';
import SalovareLoader from '../common/SalovareLoader';
import StoreAccessBlockedBanner from './StoreAccessBlockedBanner';
import AddToHomeScreenPrompt from '../common/AddToHomeScreenPrompt';
import PushNotificationPrompt from '../notifications/PushNotificationPrompt';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useSupportChat } from '../../context/SupportChatContext';
import useBillingPortalLauncher from '../../hooks/useBillingPortalLauncher';
import useCheckoutLauncher from '../../hooks/useCheckoutLauncher';
import useStoreAccessStatus from '../../hooks/useStoreAccessStatus';
import ManagementBrandTheme from '../branding/ManagementBrandTheme';
import { Permissions } from '../../constants/Permissions';
import { STAFF_ADD_TO_HOME_SCREEN_SCOPE } from '../../constants/pwaBranding';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { getStoreId, isValidGuid } from '../../utils/storeUtils';
import { isEffectivePro } from '../../utils/storePlan';
import ProUpgradeProvider from '../../context/ProUpgradeProvider';
import useStorePlan from '../../hooks/useStorePlan';
// import { useStoreRegistrationCleanup } from '../../hooks/useStoreRegistrationCleanup'; // Not needed anymore

const layoutSurfaceColor = BRAND.surface;
const contentSurfaceColor = BRAND.background;
const contentCornerRadius = 32;

const getManagementRouteStoreId = (pathname) => {
  const storeMatch = pathname.match(/^\/management\/stores\/([^/]+)/);
  if (!storeMatch) {
    return null;
  }

  return isValidGuid(storeMatch[1]) ? storeMatch[1] : null;
};

const replaceManagementRouteStoreId = (pathname, storeId) => {
  if (!storeId) {
    return pathname;
  }

  return pathname.replace(/^\/management\/stores\/[^/]+/, `/management/stores/${storeId}`);
};

const requiresActiveSubscription = (pathname) => {
  if (!pathname.startsWith('/management')) {
    return false;
  }

  if (pathname.startsWith('/management/stores-management')
    || pathname.startsWith('/management/plan-discount-vouchers')
    || pathname.startsWith('/management/payment-operations')
    || pathname.startsWith('/management/support')) {
    return false;
  }

  return true;
};

const isTeamMemberUser = (userType) => userType === 2 || userType === '2' || userType === 'TeamMember';

const AppLayout = ({ children, pageTitle, pageSubtitle }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const useCompactNavigation = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Clear store registration data when navigating away
  // useStoreRegistrationCleanup(); // Moved to StoreRegistrationPage for better control
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const { managementUser } = useAuth();
  const { waitingCount } = useSupportChat();
  const { stores, storesLoading, storesError, currentStore, currentStoreIndex, setCurrentStoreIndex } = useAppData();
  const routeStoreId = useMemo(() => getManagementRouteStoreId(location.pathname), [location.pathname]);
  const storedStoreId = getStoreId(location.pathname, location.search);
  const activeStoreId = routeStoreId || currentStore?.id || storedStoreId;
  const activeStore = useMemo(() => {
    if (!activeStoreId) {
      return currentStore;
    }

    return stores.find((store) => store.id === activeStoreId) || currentStore;
  }, [activeStoreId, currentStore, stores]);
  const { hasAnyPermission, isOwner } = usePermissions(activeStoreId);
  const { openBillingPortal, loading: billingPortalLoading } = useBillingPortalLauncher(activeStoreId);
  const { startCheckout, loading: checkoutLoading } = useCheckoutLauncher(activeStoreId);
  const shouldProtectStoreRoute = requiresActiveSubscription(location.pathname);
  const shouldLoadStoreAccess = isTeamMemberUser(managementUser?.userType) && Boolean(activeStoreId);
  const shouldCheckStoreAccess = shouldLoadStoreAccess && shouldProtectStoreRoute;
  const isWaitingForStoreResolution = shouldProtectStoreRoute && isTeamMemberUser(managementUser?.userType) && !activeStoreId && storesLoading;
  const {
    accessStatus,
    loading: accessStatusLoading,
    error: accessStatusError,
    refresh: refreshStoreAccess,
  } = useStoreAccessStatus(activeStoreId, shouldLoadStoreAccess);
  const isStoreAccessBlocked = shouldCheckStoreAccess && accessStatus?.canAccess === false;
  const isResolvingStoreAccess = isWaitingForStoreResolution
    || (shouldCheckStoreAccess && !accessStatus && !accessStatusError);
  const storeAccessContentKey = [
    accessStatus?.canAccess,
    accessStatus?.blockReason,
    accessStatus?.entitlements?.effectivePlan,
  ].join(':');
  const storeSelectable = stores.length > 1;
  const canAccessAppointments = !activeStore || hasAnyPermission([Permissions.TeamMembersAgendaRead, Permissions.TeamMembersAgendaReadOwn]);
  const canAccessMembershipPlans = !activeStore || hasAnyPermission([Permissions.MembershipsRead]);
  const canAccessProducts = !activeStore || hasAnyPermission([Permissions.ProductsRead, Permissions.StockRead]);
  const canAccessExpenses = !activeStore || hasAnyPermission([Permissions.ExpensesRead]);
  const canAccessCustomers = !activeStore || hasAnyPermission([Permissions.CustomersRead]);
  const canAccessEmployeePayroll = !activeStore || hasAnyPermission([Permissions.EmployeePaymentsRead, Permissions.EmployeePaymentsReadOwn]);
  const isAppAdmin = managementUser?.userType === 1 || managementUser?.userType === '1' || managementUser?.userType === 'AppAdmin';
  const canAccessChat = isAppAdmin || hasAnyPermission([Permissions.Chat]);
  const entitlements = accessStatus?.entitlements;
  const { isPro: storeIsPro, entitlements: planEntitlements } = useStorePlan(
    activeStoreId,
    Boolean(activeStoreId) && !isAppAdmin,
  );
  const isProPlan = isAppAdmin || isEffectivePro(entitlements) || storeIsPro;
  const planKnown = isAppAdmin || Boolean(entitlements) || Boolean(planEntitlements);
  const lockProFeatures = planKnown && !isProPlan;
  const showProfileHub = Boolean(activeStore) && !isAppAdmin && !isProPlan;
  
  // Store selection with dropdown
  const [storeSelectorOpen, setStoreSelectorOpen] = useState(false);
  const [storeSelectorAnchor, setStoreSelectorAnchor] = useState(null);

  const refreshStoreAccessRef = useRef(refreshStoreAccess);
  refreshStoreAccessRef.current = refreshStoreAccess;

  useEffect(() => {
    if (!routeStoreId || stores.length === 0) {
      return;
    }

    const routeStoreIndex = stores.findIndex((store) => store.id === routeStoreId);
    if (routeStoreIndex >= 0 && routeStoreIndex !== currentStoreIndex) {
      localStorage.setItem('currentStoreId', routeStoreId);
      setCurrentStoreIndex(routeStoreIndex);
    }
  }, [currentStoreIndex, routeStoreId, setCurrentStoreIndex, stores]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') !== 'success' || !activeStoreId) {
      return;
    }

    refreshStoreAccessRef.current();
    params.delete('checkout');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
  }, [activeStoreId, location.hash, location.pathname, location.search, navigate]);
  
  const handleStoreClick = (event) => {
    if (storeSelectable) {
      setStoreSelectorAnchor(event.currentTarget);
      setStoreSelectorOpen(true);
    }
  };
  
  const handleStoreSelect = (storeIndex) => {
    const selectedStore = stores[storeIndex];
    if (selectedStore?.id) {
      localStorage.setItem('currentStoreId', selectedStore.id);

      if (routeStoreId && routeStoreId !== selectedStore.id) {
        setStoreSelectorOpen(false);
        setStoreSelectorAnchor(null);
        navigate(`${replaceManagementRouteStoreId(location.pathname, selectedStore.id)}${location.search}${location.hash}`);
        return;
      }
    }

    setCurrentStoreIndex(storeIndex);
    setStoreSelectorOpen(false);
    setStoreSelectorAnchor(null);
  };
  
  const handleStoreSelectorClose = () => {
    setStoreSelectorOpen(false);
    setStoreSelectorAnchor(null);
  };

  useEffect(() => {
    if (!useCompactNavigation) {
      setDrawerOpen(false);
    }
  }, [useCompactNavigation]);

  const handleDrawerToggle = () => {
    if (useCompactNavigation) {
      setDrawerOpen((prev) => !prev);
    }
  };

  const membershipPlansPath = activeStore
    ? `/management/stores/${activeStore.id}/membership-plans`
    : '/management/membership-plans';

  const navigationItems = [
    { text: t('navigation.dashboard'), icon: 'Dashboard', path: '/management' },
    ...(canAccessAppointments ? [{ text: t('navigation.appointments'), icon: 'Event', path: '/management/appointments' }] : []),
    { text: t('navigation.services'), icon: 'ContentCut', path: activeStore ? `/management/stores/${activeStore.id}/services` : '/management/services' },
    ...(canAccessProducts ? [{
      text: t('navigation.products'),
      icon: 'Inventory2',
      path: activeStore ? `/management/stores/${activeStore.id}/products` : '/management/products',
      proLocked: lockProFeatures,
    }] : []),
    ...(canAccessMembershipPlans ? [{
      text: t('navigation.membershipPlans'),
      icon: 'CardMembership',
      path: membershipPlansPath,
      proLocked: lockProFeatures,
    }] : []),
    ...(canAccessCustomers ? [{
      text: t('navigation.customers'),
      icon: 'FaceRetouchingNatural',
      path: activeStore ? `/management/stores/${activeStore.id}/customers` : '/management/customers',
    }] : []),
    ...(canAccessChat ? [{
      text: waitingCount > 0
        ? `${t(isAppAdmin ? 'navigation.appSupport' : 'navigation.storeChat')} (${waitingCount})`
        : t(isAppAdmin ? 'navigation.appSupport' : 'navigation.storeChat'),
      icon: 'Chat',
      path: '/management/support',
    }] : []),
    { text: t('navigation.store'), icon: 'Store', path: '/management/stores' },
    { text: t('navigation.teamMembers'), icon: 'Groups', path: activeStore ? `/management/stores/${activeStore.id}/team-members` : '/management/team-members' },
    ...(showProfileHub ? [{
      text: t('navigation.profile'),
      icon: 'Person',
      path: `/management/stores/${activeStore.id}/profile`,
    }] : []),
    ...(activeStore && (isOwner || hasAnyPermission([Permissions.StoresNotificationSettingsUpdate])) ? [{
      text: t('navigation.communication'),
      icon: 'EditNotifications',
      path: `/management/stores/${activeStore.id}/communication`,
    }] : []),
    ...(activeStore && (isOwner || isAppAdmin) ? [{
      text: t('navigation.importData'),
      icon: 'CloudUpload',
      path: `/management/stores/${activeStore.id}/import-data`,
    }] : []),
    ...(canAccessExpenses ? [{
      text: t('navigation.expenses'),
      icon: 'ReceiptLong',
      path: activeStore ? `/management/stores/${activeStore.id}/expenses` : '/management/expenses',
      proLocked: lockProFeatures,
    }] : []),
    ...(activeStore && canAccessEmployeePayroll ? [{
      text: t('navigation.employeePayroll'),
      icon: 'Payments',
      path: `/management/stores/${activeStore.id}/employee-payments/payroll`,
      proLocked: lockProFeatures,
    }] : []),
    ...(activeStore && isOwner ? [{
      text: t('navigation.billing'),
      icon: 'AccountBalance',
      path: `/management/stores/${activeStore.id}/payment-settings`,
    }] : []),
    ...(isAppAdmin ? [
      { text: t('navigation.paymentOperations'), icon: 'MonitorHeart', path: '/management/payment-operations' },
      { text: t('navigation.storesManagement'), icon: 'AddBusiness', path: '/management/stores-management' },
    ] : []),
  ];

  const isActivePath = (path) => {
    if (path === '/management') {
      return location.pathname === '/management';
    }

    if (location.pathname === path) {
      return true;
    }

    if (path === membershipPlansPath) {
      return location.pathname.endsWith('/customer-memberships');
    }

    if (path.endsWith('/profile')) {
      return location.pathname.endsWith('/profile');
    }

    return false;
  };

  const content = isResolvingStoreAccess ? (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        px: 3,
      }}
    >
      <CircularProgress sx={{ color: BRAND.accent }} />
      <Typography variant="body2" color="text.secondary">
        {t('stores.access.checking')}
      </Typography>
    </Box>
  ) : (
    <React.Fragment key={storeAccessContentKey}>{children}</React.Fragment>
  );

  return (
    <ManagementBrandTheme>
      <ProUpgradeProvider storeId={activeStoreId} isOwner={isOwner}>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: layoutSurfaceColor }}>
        {/* Left Sidebar - Desktop */}
        <LeftSidebar
          drawerOpen={drawerOpen}
          useCompactNavigation={useCompactNavigation}
          navigationItems={navigationItems}
          isActivePath={isActivePath}
          onDrawerToggle={handleDrawerToggle}
        />

        {/* Main Content Area */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minWidth: 0,
          bgcolor: layoutSurfaceColor
        }}>
          {/* Mobile Header - Simple Store Name + Cog Icon */}
          <MobileHeader id="mobile-header"
            currentStore={activeStore}
            onDrawerToggle={handleDrawerToggle}
            onMenuOpen={() => setMobileMenuOpen(true)}
            onStoreClick={handleStoreClick}
            storeSelectable={storeSelectable}
            showBranding={isProPlan}
          />

          <AddToHomeScreenPrompt
            scopeId={STAFF_ADD_TO_HOME_SCREEN_SCOPE}
            requireStoreHost={false}
            i18nPrefix="management.addToHomeScreen"
            variant="management"
          />
          <PushNotificationPrompt audience="staff" />

          {/* Top App Bar - Desktop Only */}
          <TopAppBar id="top-app-bar"
            currentStore={activeStore}
            onDrawerToggle={handleDrawerToggle}
            onStoreClick={handleStoreClick}
            onUserProfileClick={() => {}}
            storeSelectable={storeSelectable}
            showBranding={isProPlan}
          />
          
          {/* Store Selector Menu */}
          <Menu
            anchorEl={storeSelectorAnchor}
            open={storeSelectorOpen}
            onClose={handleStoreSelectorClose}
            disableScrollLock={true}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                maxWidth: 'min(360px, calc(100vw - 32px))',
                border: `1px solid ${BRAND.line}`,
                boxShadow: `0 12px 32px ${brandAlpha('primary', 0.12)}`,
                borderRadius: BRAND_RADIUS.button
              }
            }}
          >
            {storesLoading ? (
              <MenuItem disabled sx={{ justifyContent: 'center', py: 1.5 }}>
                <SalovareLoader size="small" />
              </MenuItem>
            ) : storesError ? (
              <MenuItem disabled>
                <Typography variant="body2" color="error">
                  {t('common.error')}: {storesError}
                </Typography>
              </MenuItem>
            ) : stores.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {t('common.noStores')}
                </Typography>
              </MenuItem>
            ) : (
              stores.map((store, index) => (
                <MenuItem
                  key={store.id}
                  onClick={() => handleStoreSelect(index)}
                  selected={currentStoreIndex === index}
                  sx={{
                    color: BRAND.text,
                    whiteSpace: 'normal',
                    alignItems: 'flex-start',
                    '&.Mui-selected': {
                      bgcolor: brandAlpha('accent', 0.12),
                      color: BRAND.accentText,
                      '&:hover': { bgcolor: brandAlpha('accent', 0.18) }
                    },
                    '&:hover': {
                      bgcolor: brandAlpha('accent', 0.06)
                    }
                  }}
                >
                  <span style={{ fontSize: '1.2rem', marginRight: 8, flexShrink: 0, lineHeight: 1.4 }}>{store.logo}</span>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {store.name}
                  </Typography>
                </MenuItem>
              ))
            )}
          </Menu>

          {/* Main Content */}
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: layoutSurfaceColor
            }}
          >
            <Box
              id="main-content-backdrop"
              sx={{
                bgcolor: layoutSurfaceColor
              }}
            >
              <Box
                id="main-content"
                sx={{
                  bgcolor: contentSurfaceColor,
                  borderTopLeftRadius: { xs: 0, lg: `${contentCornerRadius}px` },
                  borderTop: { xs: 'none', lg: `1px solid ${BRAND.line}` },
                  borderLeft: { xs: 'none', lg: `1px solid ${BRAND.line}` },
                  overflow: 'hidden'
                }}
              >
                {isStoreAccessBlocked ? (
                  <StoreAccessBlockedBanner
                    blockReason={accessStatus?.blockReason}
                    isOwner={isOwner}
                    storeName={activeStore?.name}
                    onOpenBillingPortal={openBillingPortal}
                    billingPortalLoading={billingPortalLoading}
                    onStartCheckout={startCheckout}
                    checkoutLoading={checkoutLoading}
                    onRefresh={refreshStoreAccess}
                    refreshing={accessStatusLoading}
                  />
                ) : null}
                {content}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Mobile Menu - Slide Out */}
        <MobileMenu
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1999,
              display: { xs: 'block', lg: 'none' }
            }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </Box>
      </ProUpgradeProvider>
    </ManagementBrandTheme>
  );
};

export default AppLayout;
